import { ToolResult } from '../../types/tool.types';
import { ToolContext } from '../tool-context.type';

export interface DebugOptions {
	cutouts?: boolean;
	lights?: boolean;
	camera?: boolean;
	bounds?: boolean;
	showTools?: boolean;
}

export interface SetDebugModeArgs {
	enabled: boolean;
	options?: DebugOptions;
}

export async function setDebugMode(args: SetDebugModeArgs, context: ToolContext): Promise<ToolResult> {
	const { threeInstance, publish, options: contextOptions } = context;
	const { enabled, options: debugOptions } = args;

	if (typeof enabled !== 'boolean') {
		return { success: false, error: 'enabled parameter must be a boolean' };
	}

	// When enabling debug mode, all options default to true unless explicitly set to false
	// When disabling debug mode, all options are turned off
	const resolvedOptions: DebugOptions = enabled ? {
		cutouts: debugOptions?.cutouts ?? true,
		lights: debugOptions?.lights ?? true,
		camera: debugOptions?.camera ?? true,
		bounds: debugOptions?.bounds ?? true,
		showTools: debugOptions?.showTools ?? true
	} : {
		cutouts: false,
		lights: false,
		camera: false,
		bounds: false,
		showTools: false
	};

	// Update context options
	if (contextOptions) {
		(contextOptions as any).debugMode = enabled;
		(contextOptions as any).debugOptions = resolvedOptions;
	}

	// Apply to the 3D instance if available
	if (threeInstance) {
		if (typeof threeInstance.setDebugMode === 'function') {
			threeInstance.setDebugMode(enabled, resolvedOptions);
		}
	}

	publish('debug_mode', 'changed', {
		ref: 'debugMode',
		old: null,
		new: enabled,
		options: resolvedOptions
	});

	const enabledFeatures = Object.entries(resolvedOptions)
		.filter(([, value]) => value)
		.map(([key]) => key);

	return {
		success: true,
		message: enabled
			? `Debug mode enabled with: ${enabledFeatures.join(', ') || 'no features'}`
			: 'Debug mode disabled',
		data: { enabled, options: resolvedOptions }
	};
}
